import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useGetWallets } from "@/hooks/useGetWallets";
import { useIntelligenceStore } from "@/store/Intelligence";
import { useClusterStore } from "@/store/cluster";
import { useMainStore } from "@/store/main";
import { useUserStore } from "@/store/user";
import { MAP_RESOURCE_TYPE_TO_CLUSTER_TYPE } from "@/constants/cluster";
import { Cluster, ClusterType } from "@/types";
import { BalanceType } from "@/types/user";

export type GetStartedChecklistItem = {
  id: string;
  label: string;
  done: boolean;
  href?: string;
  onClick?: () => void;
};

/**
 * Onboarding checklist shared by the left sidebar and Account overview.
 * Fetches clusters via cluster store directly so checklist polling does not
 * mutate paginated cluster list filters used on `/cloud/clusters`.
 */
export function useGetStartedChecklist(active: boolean, onPurchaseCredits?: () => void) {
  const [location] = useLocation();
  const tool = useMainStore((s) => s.tool);
  const keys = useIntelligenceStore((s) => s.keys);
  const fetchKeys = useIntelligenceStore((s) => s.fetchKeys);
  const fetchClustersRequest = useClusterStore((s) => s.fetchClustersRequest);
  const [checklistClusters, setChecklistClusters] = useState<Cluster[]>([]);
  const balances = useUserStore((s) => s.balances);
  const fetchBalances = useUserStore((s) => s.fetchBalances);
  const { walletsData } = useGetWallets();

  useEffect(() => {
    if (!active) {
      return;
    }
    fetchKeys();
    void fetchBalances(true);
    void fetchClustersRequest({ status: "all", type: null })
      .then(({ results }) => setChecklistClusters(results))
      .catch(() => setChecklistClusters([]));
  }, [active, fetchKeys, fetchBalances, fetchClustersRequest]);

  const hasWallets = Boolean(walletsData?.walletAddresses?.length);
  const hasKeys = Boolean(keys?.length);
  const hasClusters = Boolean(checklistClusters.length);
  const hasCredits = (balances?.[BalanceType.CREDITS] ?? 0) > 0;
  const hasVm = Boolean(
    checklistClusters.some(
      (cluster) =>
        MAP_RESOURCE_TYPE_TO_CLUSTER_TYPE[cluster.resourceType ?? ""] === ClusterType.VMAAS
    )
  );
  const exploredCloud =
    location.startsWith("/cloud") || tool?.value === "cloud" || hasClusters;

  const earnRewardsReady = hasWallets && hasClusters;

  const items = useMemo((): GetStartedChecklistItem[] => {
    return [
      {
        id: "buy_credits",
        label: "Buy credits",
        done: hasCredits,
        onClick: onPurchaseCredits
      },
      {
        id: "explore_intelligence_api_key",
        label: "Explore io.intelligence by creating API key",
        done: hasKeys,
        href: "/ai/api-keys"
      },
      {
        id: "buy_vm",
        label: "Buy a VM on io.cloud",
        done: hasVm,
        href: "/cloud/virtual-machines"
      },
      { id: "api_key", label: "Create API Key", done: hasKeys, href: "/ai/api-keys" },
      { id: "explore_cloud", label: "Explore Cloud", done: exploredCloud, href: "/cloud/home" },
      { id: "connect_cluster", label: "Connect your cluster", done: hasClusters, href: "/cloud/clusters" },
    ];
  }, [
    hasCredits,
    hasKeys,
    hasVm,
    hasClusters,
    exploredCloud,
    onPurchaseCredits
  ]);

  return {
    items,
    hasWallets,
    hasKeys,
    hasClusters,
    hasCredits,
    hasVm,
    exploredCloud,
    earnRewardsReady
  };
}
